import React, { useState, useEffect, useMemo, useCallback, useRef, lazy, Suspense, startTransition } from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw, Info, AlertTriangle, Search, Download, ListTree, Loader2 } from 'lucide-react';
import { LanguageToggle } from './LanguageToggle';
import { IODADataPoint, IODALatencyDataPoint, CountryStatus, RegionScore, GeoAsnScore } from '../types/ioda';
import { Language } from '../types/language';
import { HistoricalService, type OutageAnnotation, type TrafficTimeseriesPoint, type TrafficByRegion } from '../services/historicalApi';
import { logger } from '../utils/logger';
import { IranMap, type TrafficByRegionItem } from './IranMap';
import { HeaderLastUpdateBlock } from './dashboard/HeaderLastUpdateBlock';
import { BannerDataFreshnessLine } from './dashboard/BannerDataFreshnessLine';

const MLInsightsPanel = lazy(() => import('./MLInsights').then((m) => ({ default: m.MLInsightsPanel })));
const DateRangePicker = lazy(() => import('./DateRangePicker').then((m) => ({ default: m.DateRangePicker })));
import { SinceThirtyFiveTimer } from './SinceThirtyFiveTimer';
import { StatusPill, scoreToNetworkStatus } from './dashboard/StatusPill';
import type { MeritChartRow, TrafficDualRow } from './dashboard/grafanaChartTypes';
import {
    LazyMeritConnectivityChartPanel,
    LazyLatencyChartPanel,
    LazyPacketLossChartPanel,
    LazyBgpChartPanel,
    LazyTrafficLevelDualChartPanel,
    LazyTrafficVolumeChartPanel,
} from './dashboard/lazyGrafanaRechartsPanels';
import { buildConnectivityCsv, downloadCsv } from '../utils/exportReport';
import { generateIrinterBriefPdf } from '../utils/exportPdf';
import type { IrinterBriefPdfResult } from '../utils/exportPdf';
import { MLService } from '../services/mlApi';
import { resolveOutageClock, formatCountedFromUtc } from '../utils/outageClockLogic';
import { getStatusTier, bannerTitle, opsStripLabel, bannerSubline, type StatusTier } from '../utils/statusVocabulary';

/** merit-nt native step (seconds); forward-fill uses same spacing */
const MERIT_BUCKET_MS = 300_000;
/** Only extrapolate when the latest real bucket is this fresh — avoids flatlining across real outages or dead ingest */
const MERIT_CARRY_STALE_MS = 45 * 60_000;
/** Chart end must track wall clock within this slack to treat the window as “live” (skip carry for historical custom ranges) */
const LIVE_WINDOW_END_SLACK_MS = 120_000;

interface GrafanaDashboardProps {
    language: Language;
    refreshRef?: React.MutableRefObject<{ refresh: () => void } | null>;
    onLastUpdate?: (timestamp: number) => void;
    onLanguageToggle?: () => void;
}

/** Outage clock uses this window always (merit-nt + ping-loss), independent of chart time range */
const OUTAGE_CLOCK_LOOKBACK_DAYS = 45;
const OUTAGE_CLOCK_LOOKBACK_SEC = OUTAGE_CLOCK_LOOKBACK_DAYS * 86400;

type MetricLocaleBlock = { plainSummary?: string; what: string; how: string; source: string; limits: string };

/* ── Metric info tooltip ── */
const METRIC_INFO: Record<string, { en: MetricLocaleBlock; fa: MetricLocaleBlock }> = {
    connectivity: {
        en: {
            plainSummary: 'Reachability from outside Iran: share of monitored /24 blocks that respond to probes vs a baseline, shown as 0–100%.',
            what: 'Percentage of active network prefixes responding to probes, relative to a stable baseline.',
            how: 'ICMP pings to a sample of /24 prefixes. The ratio of responding prefixes vs. the baseline gives a 0–100% score.',
            source: 'Collected every 5 minutes, stored locally every 2 minutes.',
            limits: 'Only measures reachability from external vantage points. Local ISP issues may not appear if upstream links remain up. Does not distinguish throttling from full outage.',
        },
        fa: {
            plainSummary: 'دسترسی از بیرون ایران: سهم بلوک‌های /24 زیر پایش که به پروب پاسخ می‌دهند نسبت به خط پایه، به صورت ۰–۱۰۰٪.',
            what: 'درصد پیشوندهای شبکه فعال که به پروب‌ها پاسخ می‌دهند، نسبت به خط پایه.',
            how: 'پینگ ICMP به نمونه‌ای از پیشوندهای /24. نسبت پاسخ‌ها به خط پایه امتیاز ۰ تا ۱۰۰٪ می‌دهد.',
            source: 'هر ۵ دقیقه جمع‌آوری و هر ۲ دقیقه ذخیره محلی.',
            limits: 'فقط دسترسی از نقاط خارجی را اندازه‌گیری می‌کند. مشکلات ISP محلی ممکن است نمایش داده نشود.',
        },
    },
    latency: {
        en: {
            plainSummary: 'How long ICMP probes take to reach Iranian /24 prefixes and return—a delay signal, not a connectivity percentage.',
            what: 'Round-trip time for ICMP probes to Iranian /24 prefixes.',
            how: 'Mean, median, and 90th-percentile latency from ping-slash24 measurements.',
            source: 'Ping-slash24-latency series. Updated every 5 minutes.',
            limits: 'Measures network-layer latency only. Application-layer delays (DNS, TLS) are not included. High packet loss can skew statistics.',
        },
        fa: {
            plainSummary: 'مدت رفت‌وبرگشت پینگ به پیشوندهای /24 ایران — سیگنال تأخیر، نه درصد اتصال.',
            what: 'زمان رفت‌وبرگشت پروب‌های ICMP به پیشوندهای /24 ایران.',
            how: 'میانگین، میانه و صدک ۹۰ تأخیر از سری ping-slash24-latency.',
            source: 'هر ۵ دقیقه به‌روزرسانی.',
            limits: 'فقط تأخیر لایه شبکه. تأخیر لایه اپلیکیشن شامل نمی‌شود.',
        },
    },
    traffic: {
        en: {
            what: 'Normalized volume of network traffic to/from Iran, as seen by global edge routers.',
            how: 'NetFlows timeseries for location=IR, normalized to 0–100%. Hourly aggregation.',
            source: 'Global CDN radar data. Fetched every 2 minutes.',
            limits: 'Covers traffic through major CDN networks. Does not represent total Iranian internet traffic. Drops can indicate filtering, routing changes, or demand shifts.',
        },
        fa: {
            what: 'حجم نرمال‌شده ترافیک شبکه به/از ایران از دید روترهای لبه جهانی.',
            how: 'سری زمانی NetFlows برای ایران، نرمال‌شده به ۰–۱۰۰٪. تجمیع ساعتی.',
            source: 'داده‌های رادار CDN جهانی. هر ۲ دقیقه واکشی.',
            limits: 'فقط ترافیک عبوری از شبکه‌های CDN بزرگ. نماینده کل ترافیک ایران نیست.',
        },
    },
    outages: {
        en: {
            what: 'Internet outages and anomalies detected in the selected period.',
            how: 'Outage annotations from global radar data, filtered to Iran.',
            source: 'Global CDN radar. Fetched every 2 minutes, stored for 90 days.',
            limits: 'Only includes outages detectable from external vantage points. Short or localized disruptions may not appear.',
        },
        fa: {
            what: 'قطعی‌ها و ناهنجاری‌های اینترنت شناسایی‌شده در بازه انتخابی.',
            how: 'یادداشت‌های قطعی از داده‌های رادار جهانی، فیلتر شده برای ایران.',
            source: 'رادار CDN جهانی. هر ۲ دقیقه واکشی، ۹۰ روز نگهداری.',
            limits: 'فقط قطعی‌های قابل تشخیص از نقاط دید خارجی.',
        },
    },
    regions: {
        en: {
            what: 'Provinces with detected outage events, ranked by regional outage score.',
            how: 'Regional outage summary for Iran. Score reflects severity × duration.',
            source: 'Fetched every 2 minutes for 24h, 7d, and 30d windows.',
            limits: 'Province attribution depends on IP geolocation accuracy. Some prefixes may be incorrectly geolocated. Score is relative, not absolute.',
        },
        fa: {
            what: 'استان‌هایی با رویدادهای قطعی شناسایی‌شده، بر اساس امتیاز منطقه‌ای.',
            how: 'خلاصهٔ قطعی منطقه‌ای برای ایران.',
            source: 'هر ۲ دقیقه واکشی.',
            limits: 'نسبت‌دهی استانی به دقت ژئولوکیشن IP بستگی دارد.',
        },
    },
    packetLoss: {
        en: {
            plainSummary: 'Share of probe packets that get no reply—loss on the measurement path, shown here as loss % (separate from merit-nt connectivity).',
            what: 'Percentage of ICMP probe packets that did not receive a response.',
            how: 'Pings to /24 prefixes; percentage of packets lost. Displayed as 0–100% (inverted: 100% = full connectivity).',
            source: 'Ping-slash24-loss series. Updated every 5 minutes.',
            limits: 'Packet loss can be caused by congestion, filtering, or routing issues. High loss does not always mean a full outage.',
        },
        fa: {
            plainSummary: 'سهم بسته‌های پروب بدون پاسخ — تلفات مسیر اندازه‌گیری، جدا از درصد اتصال merit-nt.',
            what: 'درصد بسته‌های ICMP که پاسخی دریافت نکردند.',
            how: 'پینگ به پیشوندهای /24 و محاسبهٔ درصد بسته‌های از دست رفته.',
            source: 'سری ping-slash24-loss. هر ۵ دقیقه به‌روزرسانی.',
            limits: 'تلفات بسته می‌تواند ناشی از ازدحام، فیلترینگ یا مشکلات مسیریابی باشد.',
        },
    },
    bgpRouting: {
        en: {
            plainSummary: 'BGP prefix counts — routing-table visibility, not the same as end-user reachability %.',
            what: 'This is a different measurement from the connectivity % above. BGP visibility measures routing table presence, not whether end-users can reach the internet. Number of BGP prefixes visible for Iran, indicating routing stability.',
            how: 'BGP routing tables from multiple vantage points; visible prefixes for Iranian ASNs are counted.',
            source: 'BGP series. Updated every 5 minutes.',
            limits: 'BGP visibility depends on the vantage points used. A drop in visible prefixes may indicate route withdrawals, not necessarily user-facing outages.',
        },
        fa: {
            plainSummary: 'شمارش پیشوندهای BGP — رؤیت مسیریابی، نه همان درصد دسترسی کاربر.',
            what: 'این شاخص با درصد اتصال بالا یکی نیست. رؤیت BGP حضور در جدول مسیریابی را نشان می‌دهد، نه دسترسی کاربر نهایی. تعداد پیشوندهای BGP قابل مشاهده برای ایران، نشان‌دهنده ثبات مسیریابی.',
            how: 'جداول مسیریابی BGP از نقاط مختلف؛ پیشوندهای قابل مشاهدهٔ ASNهای ایرانی شمارش می‌شوند.',
            source: 'سری BGP. هر ۵ دقیقه به‌روزرسانی.',
            limits: 'قابلیت رؤیت BGP به نقاط مشاهده بستگی دارد. کاهش پیشوندها لزوماً به معنای قطعی برای کاربران نیست.',
        },
    },
    trafficLevel: {
        en: {
            what: 'Relative traffic volume compared to normal levels, based on Google Transparency Report signals.',
            how: 'Purple line: combined traffic index (all activity types). Teal line: web-search activity only, usually in ~30-minute buckets. Both are normalized to baseline as 0–100%.',
            source: 'Public signal feeds ingested and stored locally every 2 minutes.',
            limits: 'Reflects Google-visible traffic only. The two lines can diverge when non-search traffic moves differently from search. Drops may reflect blocking or demand shifts, not total national connectivity. Long windows (7D/30D/45D) need the poller to retain traffic history (default 45 days server-side).',
        },
        fa: {
            what: 'حجم ترافیک نسبی نسبت به خط پایه بر اساس سیگنال‌های گزارش شفافیت گوگل.',
            how: 'خط بنفش: شاخص ترافیک ترکیبی (همه انواع فعالیت). خط فیروزه‌ای: فقط فعالیت جستجوی وب، معمولاً در بازه‌های حدود ۳۰ دقیقه‌ای. هر دو ۰–۱۰۰٪ نرمال‌شده نسبت به خط پایه.',
            source: 'فیدهای سیگنال عمومی؛ واکشی و ذخیره محلی هر ۲ دقیقه.',
            limits: 'فقط ترافیک قابل مشاهده توسط گوگل. دو خط می‌توانند متفاوت باشند. کاهش لزوماً معادل قطعی کل اینترنت نیست. بازه‌های ۷/۳۰/۴۵ روز به نگهداری تاریخچه در سرویس جمع‌آوری داده وابسته است (پیش‌فرض ۴۵ روز).',
        },
    },
    geoTraffic: {
        en: {
            what: 'Percentage of Iranian internet traffic by province.',
            how: 'Top locations data with location=IR. When province-level data is unavailable, reference estimates are shown.',
            source: 'Global CDN radar data. Fetched every 2 minutes.',
            limits: 'Based on CDN-routed traffic only. Actual distribution across all traffic may differ. Reference data is approximate.',
        },
        fa: {
            what: 'درصد ترافیک اینترنت ایران به تفکیک استان.',
            how: 'داده‌های مکان‌های برتر با فیلتر ایران. در صورت عدم دسترسی به داده استانی، تخمین‌های مرجع نمایش داده می‌شود.',
            source: 'داده‌های رادار CDN جهانی. هر ۲ دقیقه واکشی.',
            limits: 'فقط ترافیک عبوری از شبکه CDN. توزیع واقعی ممکن است متفاوت باشد.',
        },
    },
};

function MetricTooltip({ metricKey, isFa }: { metricKey: string; isFa: boolean }) {
    const [open, setOpen] = useState(false);
    const [more, setMore] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    const info = METRIC_INFO[metricKey];
    if (!info) return null;
    const t = isFa ? info.fa : info.en;
    const lead = t.plainSummary ?? t.what;

    useEffect(() => {
        if (!open) return;
        const handler = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    useEffect(() => {
        if (!open) setMore(false);
    }, [open]);

    return (
        <div className="relative inline-block" ref={ref}>
            <button
                type="button"
                onClick={() => startTransition(() => setOpen(!open))}
                className="p-1 rounded-none hover:bg-ir-elevated transition-colors duration-100 text-ir-faint hover:text-ir-muted"
                aria-label={isFa ? 'اطلاعات شاخص' : 'Metric information'}
                aria-expanded={open}
            >
                <Info className="w-3.5 h-3.5" />
            </button>
            {open && (
                <div className="absolute z-50 top-full mt-1 right-0 w-80 bg-ir-elevated border border-ir-border p-4 shadow-2xl text-xs leading-relaxed rounded-none">
                    <p className="font-sans text-ir-text">{lead}</p>
                    <button
                        type="button"
                        onClick={() => startTransition(() => setMore((m) => !m))}
                        className="mt-2 text-[10px] font-sans uppercase tracking-wider text-ir-blue hover:underline"
                    >
                        {more ? (isFa ? '− بستن' : '− Less') : (isFa ? '+ بیشتر' : '+ More')}
                    </button>
                    {more && (
                        <div className="mt-3 space-y-2 border-t border-ir-border pt-3 font-sans text-ir-muted">
                            <div><span className="text-ir-muted font-medium uppercase tracking-wider text-[10px]">{isFa ? 'چه چیزی (کامل)' : 'What (full)'}</span> <span className="text-ir-text">{t.what}</span></div>
                            <div><span className="text-ir-muted font-medium uppercase tracking-wider text-[10px]">{isFa ? 'چگونه' : 'How'}</span> <span className="text-ir-text">{t.how}</span></div>
                            <div><span className="text-ir-muted font-medium uppercase tracking-wider text-[10px]">{isFa ? 'منبع' : 'Source'}</span> <span className="text-ir-text">{t.source}</span></div>
                            <div><span className="text-ir-muted font-medium uppercase tracking-wider text-[10px]">{isFa ? 'محدودیت‌ها' : 'Limitations'}</span> <span className="text-ir-text">{t.limits}</span></div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

/* ── Section wrapper ── */
function Section({ children, className = '', id }: { children: React.ReactNode; className?: string; id?: string }) {
    return (
        <div
            id={id}
            className={`rounded-none border border-ir-border bg-ir-surface transition-[border-color,background-color] duration-150 hover:border-slate-500/35 hover:bg-ir-elevated/20 ${className}`}
        >
            {children}
        </div>
    );
}

function statusDotClass(tier: StatusTier): string {
    if (tier === 'outage') return 'bg-ir-red';
    if (tier === 'degraded') return 'bg-ir-amber';
    return 'bg-ir-green';
}

/** Outage report row: UTC meta line for scan hierarchy */
function formatOutageReportUtc(iso: string, isFa: boolean): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    if (isFa) {
        return `${d.toLocaleString('fa-IR', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'UTC' })} UTC`;
    }
    const dateStr = d.toLocaleDateString('en-GB', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
    const hm = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'UTC' });
    return `${dateStr} · ${hm} UTC`;
}

function humanizeOutageClassification(raw: string | null, isFa: boolean): string {
    if (!raw?.trim()) return '—';
    const key = raw.trim().toUpperCase().replace(/\s+/g, '_');
    if (isFa) {
        const fa: Record<string, string> = {
            GOVERNMENT_DIRECTED: 'قطع دستوری دولتی',
            NATIONWIDE: 'سراسری',
        };
        return fa[key] ?? raw.replace(/_/g, ' ').trim();
    }
    const en: Record<string, string> = {
        GOVERNMENT_DIRECTED: 'Government-directed shutdown',
        NATIONWIDE: 'Nationwide',
    };
    if (en[key]) return en[key];
    return raw
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase());
}

function SectionTitle({ icon, title, metricKey, isFa, badge, level = 2 }: {
    icon: React.ReactNode; title: string; metricKey?: string; isFa: boolean; badge?: React.ReactNode;
    /** 1 = primary briefing sections (12px / 600 / 0.12em); 2 = card / panel titles (11px / 500 / 0.10em) */
    level?: 1 | 2;
}) {
    return (
        <div className="flex items-center justify-between gap-3 pt-2 mb-4">
            <div className="flex min-w-0 items-center gap-2">
                {icon}
                <h2
                    className={
                        level === 1
                            ? 'text-xs font-semibold uppercase tracking-[0.12em] text-ir-text'
                            : 'text-[11px] font-medium uppercase tracking-[0.1em]'
                    }
                    style={level === 2 ? { color: 'var(--text-secondary)' } : undefined}
                >
                    {title}
                </h2>
            </div>
            <div className="ml-2.5 flex shrink-0 items-center gap-2">
                {badge}
                {metricKey && <MetricTooltip metricKey={metricKey} isFa={isFa} />}
            </div>
        </div>
    );
}

/* ── Main component ── */
export const GrafanaDashboard: React.FC<GrafanaDashboardProps> = ({ language, refreshRef, onLastUpdate, onLanguageToggle }) => {
    const isFa = language === 'fa';

    const [timeRange, setTimeRange] = useState<'1h' | '6h' | '24h' | '7d' | '30d' | '45d'>('24h');
    const [customStart, setCustomStart] = useState<Date | null>(null);
    const [customEnd, setCustomEnd] = useState<Date | null>(null);
    const [refreshTick, setRefreshTick] = useState(0);

    const [connectivityData, setConnectivityData] = useState<IODADataPoint[]>([]);
    const [latencyData, setLatencyData] = useState<IODALatencyDataPoint[]>([]);
    /** Raw network score (ping-slash24-loss → connectivity %); packet-loss chart derives loss% = 100 − value */
    const [networkScoreSeries, setNetworkScoreSeries] = useState<IODADataPoint[]>([]);
    const [bgpData, setBgpData] = useState<IODADataPoint[]>([]);
    const [trafficLevelData, setTrafficLevelData] = useState<IODADataPoint[]>([]);
    const [webSearchTrafficData, setWebSearchTrafficData] = useState<IODADataPoint[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [lastUpdate, setLastUpdate] = useState(Date.now());
    const [realtimeStatus, setRealtimeStatus] = useState<CountryStatus | null>(null);
    const [regionData, setRegionData] = useState<RegionScore[]>([]);
    const [geoAsnData, setGeoAsnData] = useState<GeoAsnScore[]>([]);
    const [apiError, setApiError] = useState<string | null>(null);
    type SeriesErrorKey = 'connectivity' | 'latency' | 'packetLoss' | 'bgp' | 'gtr' | 'gtrWebSearch' | 'status';
    const [seriesErrors, setSeriesErrors] = useState<Partial<Record<SeriesErrorKey, string>>>({});
    const [outageAnnotations, setOutageAnnotations] = useState<OutageAnnotation[]>([]);
    const [trafficTimeseriesData, setTrafficTimeseriesData] = useState<TrafficTimeseriesPoint[]>([]);
    const [trafficByRegionData, setTrafficByRegionData] = useState<TrafficByRegion[]>([]);
    const [isLoadingExtra, setIsLoadingExtra] = useState(false);
    const [dcFilter, setDcFilter] = useState('');
    const [customRangeOpen, setCustomRangeOpen] = useState(false);
    const [pickerStart, setPickerStart] = useState<Date | null>(null);
    const [pickerEnd, setPickerEnd] = useState<Date | null>(null);

    const [outageClockMerit, setOutageClockMerit] = useState<IODADataPoint[]>([]);
    const [outageClockPing, setOutageClockPing] = useState<IODADataPoint[]>([]);
    const [outageClockLoading, setOutageClockLoading] = useState(true);
    const [outageClockError, setOutageClockError] = useState<string | null>(null);

    const chartCaptureRef = useRef<HTMLDivElement | null>(null);
    const [pdfExporting, setPdfExporting] = useState(false);
    type ToastState =
        | null
        | { kind: 'csv-ok'; filename: string }
        | { kind: 'pdf-gen' }
        | { kind: 'pdf-ok'; filename: string }
        | { kind: 'pdf-err'; message: string; expanded?: boolean }
        | { kind: 'pdf-warn'; text: string };
    const [toast, setToast] = useState<ToastState>(null);

    useEffect(() => {
        if (!toast) return;
        if (toast.kind === 'pdf-err') return;
        if (toast.kind === 'pdf-gen') return;
        const ms = toast.kind === 'pdf-warn' ? 8000 : 4000;
        const t = window.setTimeout(() => setToast(null), ms);
        return () => window.clearTimeout(t);
    }, [toast]);

    /* ── Time range ── */
    const { startTime, endTime } = useMemo(() => {
        const now = Date.now();
        if (customStart && customEnd) return { startTime: Math.floor(customStart.getTime() / 1000), endTime: Math.floor(customEnd.getTime() / 1000) };
        const ms: Record<string, number> = {
            '1h': 3600e3,
            '6h': 6 * 3600e3,
            '24h': 86400e3,
            '7d': 7 * 86400e3,
            '30d': 30 * 86400e3,
            '45d': 45 * 86400e3,
        };
        return { startTime: Math.floor((now - (ms[timeRange] ?? 86400e3)) / 1000), endTime: Math.floor(now / 1000) };
    }, [timeRange, customStart, customEnd, refreshTick]);

    const startTimeRef = useRef(startTime);
    const endTimeRef = useRef(endTime);
    startTimeRef.current = startTime;
    endTimeRef.current = endTime;

    /* ── Data fetching ── */
    const fetchData = useCallback(async () => {
        try {
            setIsLoading(true);
            setApiError(null);
            setSeriesErrors({});
            const now = Math.floor(Date.now() / 1000);
            const useCustom = Boolean(customStart && customEnd);
            const st = useCustom ? startTimeRef.current : now - (endTimeRef.current - startTimeRef.current);
            const et = useCustom ? endTimeRef.current : now;

            const coreRes = await HistoricalService.getDashboardCore(st, et);
            let scoreData: IODADataPoint[] = [];
            let lossData: IODADataPoint[] = [];
            let latencyResData: IODALatencyDataPoint[] = [];
            let statusPayload: CountryStatus | null = null;
            let bgpResData: IODADataPoint[] = [];
            let gtrResData: IODADataPoint[] = [];
            let gtrWebSearchResData: IODADataPoint[] = [];
            const nextSeries: Partial<Record<SeriesErrorKey, string>> = {};

            if (coreRes.data && !coreRes.error) {
                const b = coreRes.data;
                scoreData = b.meritNt.data;
                lossData = b.networkScore.data;
                latencyResData = b.latency.data;
                statusPayload = b.status;
                bgpResData = b.bgp.data;
                gtrResData = b.gtrNorm.data;
                gtrWebSearchResData = b.gtrNormWebSearch?.data ?? [];
            } else {
                const stWide = Math.min(st, now - 6 * 3600);
                const [scoreRes, lossRes, latencyRes, statusRes, bgpRes, gtrRes, gtrWsRes] = await Promise.all([
                    HistoricalService.getConnectivityData(st, et, 'merit-nt'),
                    HistoricalService.getNetworkScoreData(st, et),
                    HistoricalService.getLatencyData(st, et),
                    HistoricalService.getStatus(),
                    HistoricalService.getConnectivityData(stWide, et, 'bgp'),
                    HistoricalService.getConnectivityData(stWide, et, 'gtr-norm'),
                    HistoricalService.getConnectivityData(stWide, et, 'gtr-norm-web-search'),
                ]);
                scoreData = scoreRes.data;
                lossData = lossRes.data;
                latencyResData = latencyRes.data;
                statusPayload = statusRes.data;
                bgpResData = bgpRes.data;
                gtrResData = gtrRes.data;
                gtrWebSearchResData = gtrWsRes.data;
                if (scoreRes.error) nextSeries.connectivity = scoreRes.error;
                if (lossRes.error) nextSeries.packetLoss = lossRes.error;
                if (latencyRes.error) nextSeries.latency = latencyRes.error;
                if (bgpRes.error) nextSeries.bgp = bgpRes.error;
                if (gtrRes.error) nextSeries.gtr = gtrRes.error;
                if (gtrWsRes.error) nextSeries.gtrWebSearch = gtrWsRes.error;
                if (statusRes.error) nextSeries.status = statusRes.error;
                if (coreRes.error && coreRes.error !== 'dashboard-core HTTP 404') {
                    logger.warn('dashboard-core unavailable, used parallel fetch:', coreRes.error);
                }
            }

            if (scoreData.length === 0) {
                const fb = await HistoricalService.getConnectivityData(now - 3 * 3600, et, 'merit-nt');
                scoreData = fb.data;
                if (fb.error) nextSeries.connectivity = fb.error;
            }
            if (lossData.length === 0) {
                const lf = await HistoricalService.getNetworkScoreData(now - 3 * 3600, et);
                lossData = lf.data;
                if (lf.error) nextSeries.packetLoss = lf.error;
            }

            setSeriesErrors(nextSeries);
            if (scoreData.length === 0) {
                setApiError(nextSeries.connectivity ?? 'Connectivity data unavailable');
            } else {
                setApiError(null);
            }

            setConnectivityData(scoreData);
            setNetworkScoreSeries(lossData);
            setLatencyData(latencyResData);
            setBgpData(bgpResData);
            setTrafficLevelData(gtrResData);
            setWebSearchTrafficData(gtrWebSearchResData);
            setRealtimeStatus(statusPayload);
            const ts = Date.now();
            setLastUpdate(ts);
            onLastUpdate?.(ts);
        } catch (error) {
            logger.error('Error fetching data:', error);
            setApiError(error instanceof Error ? error.message : String(error));
        } finally {
            setIsLoading(false);
        }
    }, [customStart, customEnd, onLastUpdate]);

    const fetchOutageClockData = useCallback(async () => {
        setOutageClockLoading(true);
        setOutageClockError(null);
        try {
            const now = Math.floor(Date.now() / 1000);
            const from = now - OUTAGE_CLOCK_LOOKBACK_SEC;
            const [meritRes, pingRes] = await Promise.all([
                HistoricalService.getConnectivityData(from, now, 'merit-nt'),
                HistoricalService.getNetworkScoreData(from, now),
            ]);
            const errs = [meritRes.error, pingRes.error].filter(Boolean) as string[];
            if (errs.length > 0) setOutageClockError(errs.join(' · '));
            setOutageClockMerit(meritRes.data);
            setOutageClockPing(pingRes.data);
        } catch (error) {
            logger.error('Outage clock fetch failed:', error);
            setOutageClockError(error instanceof Error ? error.message : String(error));
        } finally {
            setOutageClockLoading(false);
        }
    }, []);

    const fetchExtra = useCallback(async () => {
        setIsLoadingExtra(true);
        try {
            const now = Math.floor(Date.now() / 1000);
            const useCustom = Boolean(customStart && customEnd);
            const st = useCustom ? startTimeRef.current : now - (endTimeRef.current - startTimeRef.current);
            const et = useCustom ? endTimeRef.current : now;

            const stWide = Math.min(st, now - 6 * 3600);
            const [outRes, trRes, regRes, regionsRes, geoRes] = await Promise.all([
                HistoricalService.getOutageAnnotations(50),
                HistoricalService.getTrafficTimeseries(stWide, et),
                HistoricalService.getTrafficByRegion(),
                HistoricalService.getRegionData(st, et),
                HistoricalService.getGeoAsnData(st, et),
            ]);
            const extraErrs = [outRes, trRes, regRes, regionsRes, geoRes].map((r) => r.error).filter(Boolean) as string[];
            if (extraErrs.length > 0) {
                logger.warn('Extra data partial failure:', extraErrs.join(' · '));
            }
            setOutageAnnotations(outRes.data);
            setTrafficTimeseriesData(trRes.data);
            setTrafficByRegionData(regRes.data);
            setRegionData(regionsRes.data);
            setGeoAsnData(geoRes.data);
        } catch (err) {
            logger.warn('Failed to fetch extra data:', err);
        } finally {
            setIsLoadingExtra(false);
        }
    }, [customStart, customEnd]);

    useEffect(() => {
        if (!refreshRef) return;
        refreshRef.current = { refresh: () => startTransition(() => setRefreshTick((t) => t + 1)) };
        return () => { refreshRef.current = null; };
    }, [refreshRef]);
    useEffect(() => { fetchData(); fetchExtra(); }, [fetchData, fetchExtra, startTime, endTime]);
    useEffect(() => { void fetchOutageClockData(); }, [fetchOutageClockData, refreshTick]);

    /* ── Derived data — trim incomplete trailing measurement buckets (max 2; 3+ = real signal) ── */
    const trimTrailing = <T extends { value: number }>(data: T[]): T[] => {
        if (data.length < 3) return data;
        const maxTrim = Math.min(6, Math.floor(data.length * 0.4));
        const bodyEnd = data.length - maxTrim;
        const body = data.slice(0, bodyEnd);
        if (body.length < 2) return data;
        const vals = body.map(d => d.value).sort((a, b) => a - b);
        const median = vals[Math.floor(vals.length / 2)];
        const threshold = Math.max(median * 0.35, 5);
        let end = data.length - 1;
        let trimmed = 0;
        while (end >= bodyEnd && end >= 1) {
            if (Math.abs(data[end].value - median) > threshold) {
                trimmed++;
                if (trimmed > 2) break;
                end--;
            } else break;
        }
        return data.slice(0, end + 1);
    };

    /** Merit chart: do not median-trim tail — sustained outages read as "outliers" and can hide the latest hour+. */
    const chartData = useMemo(() => {
        return connectivityData
            .filter(p => p && Number.isFinite(p.value) && Number.isFinite(p.from))
            .map(p => {
                const value = Math.max(0, Math.min(100, p.value));
                return {
                    timestamp: p.from * 1000,
                    value,
                    outageFill: value < 35 ? value : null,
                };
            })
            .sort((a, b) => a.timestamp - b.timestamp);
    }, [connectivityData]);

    /**
     * Plot series: real samples plus 5m forward-fill of the last value to “now” when the latest buckets are not yet published.
     * Historical custom ranges (end ≪ now) skip carry. Stats / CSV still use `chartData` (observed only).
     */
    const meritChartPlotData = useMemo((): MeritChartRow[] => {
        const base: MeritChartRow[] = chartData.map((p) => ({
            ...p,
            carriedForward: false,
        }));
        if (base.length === 0) return base;

        const nowMs = Date.now();
        const windowEndMs = Math.min(nowMs, endTime * 1000);
        const liveWindow = endTime * 1000 >= nowMs - LIVE_WINDOW_END_SLACK_MS;
        const last = base[base.length - 1]!;

        if (!liveWindow || windowEndMs <= last.timestamp) return base;
        if (nowMs - last.timestamp > MERIT_CARRY_STALE_MS) return base;

        const out = [...base];
        let t = last.timestamp + MERIT_BUCKET_MS;
        while (t <= windowEndMs) {
            out.push({
                timestamp: t,
                value: last.value,
                outageFill: last.outageFill,
                carriedForward: true,
            });
            t += MERIT_BUCKET_MS;
        }
        return out;
    }, [chartData, endTime, refreshTick]);

    /** Extend axis to wall-clock now so missing ingest shows as empty tail (bucket times are UTC ms). */
    const connectivityXDomain = useMemo((): [number, number] | undefined => {
        if (meritChartPlotData.length === 0) return undefined;
        const minT = meritChartPlotData[0].timestamp;
        const lastT = meritChartPlotData[meritChartPlotData.length - 1].timestamp;
        const nowMs = Date.now();
        return [minT, Math.max(lastT, nowMs)];
    }, [meritChartPlotData]);

    const latencyChartData = useMemo(() => {
        const sorted = latencyData
            .filter(d => d && Number.isFinite(d.from ?? 0) && Number.isFinite(d.meanLatency ?? 0))
            .map(d => ({ timestamp: (d.from ?? 0) * 1000, value: d.meanLatency ?? 0, mean: d.meanLatency ?? 0, median: d.medianLatency ?? 0, p90: d.p90Latency ?? 0 }))
            .sort((a, b) => a.timestamp - b.timestamp);
        return trimTrailing(sorted);
    }, [latencyData]);

    const trafficChartData = useMemo(() => {
        return trafficTimeseriesData
            .filter(d => d && Number.isFinite(d.from) && Number.isFinite(d.value))
            .map(d => ({ timestamp: d.from * 1000, value: Number(d.value) * 100 }))
            .sort((a, b) => a.timestamp - b.timestamp);
    }, [trafficTimeseriesData]);

    /** Loss % from ping-slash24-loss connectivity (stored value = 100 − loss_pct) */
    const packetLossChartData = useMemo(() => {
        const lossSeries = networkScoreSeries
            .filter(p => p && Number.isFinite(p.value) && Number.isFinite(p.from))
            .map(p => ({
                ...p,
                value: Math.max(0, Math.min(100, 100 - p.value)),
            }));
        return trimTrailing(
            lossSeries
                .map(p => ({ timestamp: p.from * 1000, value: p.value }))
                .sort((a, b) => a.timestamp - b.timestamp)
        );
    }, [networkScoreSeries]);

    const bgpChartData = useMemo(() => {
        return trimTrailing(
            bgpData
                .filter(p => p && Number.isFinite(p.value) && Number.isFinite(p.from))
                .map(p => ({ timestamp: p.from * 1000, value: p.value }))
                .sort((a, b) => a.timestamp - b.timestamp)
        );
    }, [bgpData]);

    const latestBgpPrefixes = useMemo(() => {
        if (bgpChartData.length === 0) return null;
        return bgpChartData[bgpChartData.length - 1].value;
    }, [bgpChartData]);

    const trafficLevelChartData = useMemo(() => {
        return trimTrailing(
            trafficLevelData
                .filter(p => p && Number.isFinite(p.value) && Number.isFinite(p.from))
                .map(p => ({ timestamp: p.from * 1000, value: Math.max(0, Math.min(100, p.value)) }))
                .sort((a, b) => a.timestamp - b.timestamp)
        );
    }, [trafficLevelData]);

    const webSearchLevelChartData = useMemo(() => {
        return trimTrailing(
            webSearchTrafficData
                .filter(p => p && Number.isFinite(p.value) && Number.isFinite(p.from))
                .map(p => ({ timestamp: p.from * 1000, value: Math.max(0, Math.min(100, p.value)) }))
                .sort((a, b) => a.timestamp - b.timestamp)
        );
    }, [webSearchTrafficData]);

    const trafficLevelDualChartData = useMemo((): TrafficDualRow[] => {
        const byTs = new Map<number, TrafficDualRow>();
        for (const p of trafficLevelChartData) {
            byTs.set(p.timestamp, { timestamp: p.timestamp, allTraffic: p.value, webSearch: null });
        }
        for (const p of webSearchLevelChartData) {
            const row = byTs.get(p.timestamp);
            if (row) row.webSearch = p.value;
            else byTs.set(p.timestamp, { timestamp: p.timestamp, allTraffic: null, webSearch: p.value });
        }
        return [...byTs.values()].sort((a, b) => a.timestamp - b.timestamp);
    }, [trafficLevelChartData, webSearchLevelChartData]);

    const stats = useMemo(() => {
        if (chartData.length === 0) return null;
        const values = chartData.map(d => d.value);
        const current = values[values.length - 1];
        const avg = values.reduce((a, b) => a + b, 0) / values.length;
        const min = Math.min(...values);
        const max = Math.max(...values);
        return { current, avg, min, max };
    }, [chartData]);

    const affectedRegionCount = useMemo(() => regionData.filter(r => r.score > 100 || r.event_cnt > 0).length, [regionData]);

    const filteredGeoAsnData = useMemo(() => {
        if (!dcFilter.trim()) return geoAsnData.slice(0, 20);
        const q = dcFilter.toLowerCase();
        return geoAsnData.filter(r =>
            (r.entity_name ?? '').toLowerCase().includes(q) ||
            (r.asn ?? '').toLowerCase().includes(q) ||
            (r.org ?? '').toLowerCase().includes(q)
        ).slice(0, 20);
    }, [geoAsnData, dcFilter]);

    const trafficByProvinceData = useMemo<TrafficByRegionItem[]>(() => {
        const byRegion = new Map<string, { regionName: string; regionCode: string; value: number }>();
        for (const d of trafficByRegionData) {
            const key = (d.regionName || d.regionCode || '').trim();
            if (!key) continue;
            const v = Number(d.value);
            const existing = byRegion.get(key);
            if (!existing || v > existing.value) byRegion.set(key, { regionName: d.regionName ?? key, regionCode: d.regionCode ?? key, value: v });
        }
        return Array.from(byRegion.values()).sort((a, b) => b.value - a.value);
    }, [trafficByRegionData]);

    /* ── Formatters ── */
    const formatXAxis = (ts: number) => {
        const d = new Date(ts);
        const hours = (Date.now() - ts) / 3600e3;
        const loc = isFa ? 'fa-IR' : 'en-GB';
        if (hours < 48) {
            return d.toLocaleTimeString(loc, {
                timeZone: 'UTC',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
            });
        }
        return d.toLocaleDateString(loc, { timeZone: 'UTC', month: 'short', day: 'numeric' });
    };

    const timeLabel = customStart && customEnd ? (isFa ? 'بازه دلخواه' : 'Custom') :
        timeRange === '1h' ? '1H' : timeRange === '6h' ? '6H' : timeRange === '24h' ? '24H' : timeRange === '7d' ? '7D' : timeRange === '45d' ? '45D' : '30D';

    /* ── Status config (single vocabulary: banner · ops strip · maps to StatusPill tiers) ── */
    const status = realtimeStatus?.currentStatus ?? 'normal';
    const connectivity = realtimeStatus?.connectivity ?? (stats?.current ?? 100);
    const tier = getStatusTier(status, connectivity);
    const isCritical = tier === 'outage';
    const isDegraded = tier === 'degraded';

    /** Banner accent from live connectivity (flat bar; left border + dot only) */
    const atmosphereBannerClass =
        connectivity < 35 ? 'status-banner--outage' : connectivity <= 60 ? 'status-banner--degraded' : 'status-banner--normal';

    const statusBannerTitle = bannerTitle(tier, isFa);
    const statusSubline = bannerSubline(tier, isFa);

    const opsStatusLabel = opsStripLabel(tier, isFa);
    const opsStatusDot = isCritical ? 'bg-ir-red' : isDegraded ? 'bg-ir-amber' : 'bg-ir-green';

    const handleExportCsv = useCallback(async () => {
        const topIsps = geoAsnData
            .slice(0, 10)
            .map((g) => (g.org ?? g.asn ?? g.entity_name ?? g.entity_code) as string)
            .filter(Boolean);
        let mlNote = '';
        try {
            const h = await MLService.getHealthScore(24);
            if (h) mlNote = `health_score=${Math.round(h.score)} trend=${h.trend} regime=${h.regime ?? ''}`;
        } catch {
            /* ML optional */
        }
        const rows = chartData.map((d) => ({ timestamp: d.timestamp, value: d.value }));
        const csv = buildConnectivityCsv(rows, realtimeStatus, topIsps, mlNote || null);
        const d = new Date();
        const fn = `irinter-export-${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}.csv`;
        downloadCsv(csv, fn);
        setToast({ kind: 'csv-ok', filename: fn });
    }, [chartData, realtimeStatus, geoAsnData]);

    const handleExportPdf = useCallback(async () => {
        setPdfExporting(true);
        setToast({ kind: 'pdf-gen' });
        try {
            const cur = stats?.current ?? connectivity;
            const clock = resolveOutageClock(outageClockMerit, outageClockPing, cur);
            let outageClockLine = isFa ? 'ساعت قطع: —' : 'Outage clock: —';
            let countedFromLine: string | null = null;
            if (clock?.mode === 'active_outage') {
                countedFromLine = `${isFa ? 'شمارش از' : 'Counted from:'} ${formatCountedFromUtc(clock.outageStartMs, isFa)}`;
                outageClockLine = isFa
                    ? `زیر ${clock.threshold}% (مرتبط با ساعت قطع در بالای صفحه)`
                    : `Below ~${clock.threshold}% threshold (see outage clock above)`;
            } else if (clock?.mode === 'recovery') {
                outageClockLine = isFa ? 'فاز بهبود · بالای آستانه فعلی' : 'Recovery phase · above current threshold';
            }
            let mlHealth: { score: number; trend: string } | null = null;
            try {
                const h = await MLService.getHealthScore(24);
                if (h) mlHealth = { score: h.score, trend: h.trend };
            } catch {
                /* optional */
            }
            const methodologyNote = `${METRIC_INFO.connectivity.en.what} ${METRIC_INFO.connectivity.en.limits}`;
            const result: IrinterBriefPdfResult = await generateIrinterBriefPdf({
                isFa,
                generatedAtUtc: new Date(),
                status: realtimeStatus,
                countedFromLine,
                outageClockLine,
                connectivityPct: cur,
                topProvinces: regionData,
                topAsns: geoAsnData,
                mlHealth,
                methodologyNote,
                chartElement: chartCaptureRef.current,
            });
            if (!result.chartImageCaptured) {
                setToast({
                    kind: 'pdf-warn',
                    text: isFa
                        ? 'PDF ذخیره شد — تصویر نمودار گرفته نشد. صفحه را تازه‌سازی کنید و دوباره صادر کنید.'
                        : 'PDF saved — chart image not captured. Try refreshing and exporting again.',
                });
            } else {
                setToast({ kind: 'pdf-ok', filename: result.filename });
            }
        } catch (e) {
            logger.error('PDF export failed', e);
            const msg = e instanceof Error ? e.message : String(e);
            setToast({ kind: 'pdf-err', message: msg, expanded: false });
        } finally {
            setPdfExporting(false);
        }
    }, [isFa, stats, connectivity, outageClockMerit, outageClockPing, realtimeStatus, regionData, geoAsnData]);

    return (
        <div className="flex-1 bg-ir-bg text-ir-text">
            {/* ── TOP BAR — taller bar, readable type (export: footer only) ── */}
            <header className="sticky top-0 z-30 min-h-[3.75rem] sm:min-h-16 border-b border-ir-border bg-ir-surface/95 backdrop-blur-sm py-2 sm:py-2.5">
                <div className="max-w-[1440px] mx-auto px-5 min-[1280px]:px-8 min-h-[inherit] flex items-center justify-between gap-3 sm:gap-4">
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0 shrink-0">
                        <span className="font-display text-lg sm:text-xl md:text-2xl font-semibold text-ir-text tracking-tight whitespace-nowrap">
                            IRINTER
                        </span>
                        <span className="hidden sm:inline text-ir-border select-none text-lg sm:text-xl" aria-hidden>
                            |
                        </span>
                        <span className="hidden md:inline text-xs sm:text-sm uppercase tracking-[0.12em] text-ir-muted font-sans whitespace-nowrap">
                            {isFa ? 'مانیتور اینترنت ایران' : 'Iran Internet Monitor'}
                        </span>
                        <Link
                            to="/incidents"
                            className="hidden md:inline-flex items-center text-xs sm:text-sm uppercase tracking-wider text-ir-blue hover:text-ir-text border border-ir-border px-2.5 py-1.5 ml-1"
                        >
                            {isFa ? 'قطعی‌ها' : 'Incidents'}
                        </Link>
                    </div>

                    <div className="hidden lg:flex items-center justify-center flex-1 min-w-0 px-4">
                        <div
                            className="inline-flex items-center gap-2.5 px-4 py-2 border border-ir-border bg-ir-bg/80 rounded-none"
                            title={opsStatusLabel}
                        >
                            <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${opsStatusDot}`} aria-hidden />
                            <span className="text-xs sm:text-sm uppercase tracking-wider text-ir-muted font-sans font-medium">
                                {opsStatusLabel}
                            </span>
                        </div>
                    </div>

                    <div className="flex items-center gap-2 sm:gap-4 shrink-0">
                        <HeaderLastUpdateBlock lastUpdate={lastUpdate} isFa={isFa} />
                        {onLanguageToggle && <LanguageToggle language={language} onToggle={onLanguageToggle} />}
                        <div className="hidden sm:flex flex-col items-end gap-1">
                            <button
                                type="button"
                                onClick={() => startTransition(() => setRefreshTick((t) => t + 1))}
                                className="flex items-center gap-2 px-3 py-2 border border-ir-border text-ir-muted hover:text-ir-text hover:border-ir-blue/50 bg-transparent text-sm font-sans uppercase tracking-wider transition-colors duration-100"
                            >
                                <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                                <span>{isFa ? 'همگام‌سازی' : 'Sync'}</span>
                            </button>
                            <span className="text-xs font-sans normal-case tracking-normal text-ir-faint max-w-[12rem] text-right leading-snug">
                                {isFa ? 'هم‌اکنون · خودکار ~۲ دقیقه' : 'Sync now · auto every ~2 min'}
                            </span>
                        </div>
                        <button
                            type="button"
                            onClick={() => startTransition(() => setRefreshTick((t) => t + 1))}
                            aria-label={isFa ? 'همگام‌سازی داده‌ها' : 'Refresh data'}
                            title={isFa ? 'همگام‌سازی' : 'Sync'}
                            className="flex sm:hidden items-center gap-2 px-3 py-2 border border-ir-border text-ir-muted hover:text-ir-text hover:border-ir-blue/50 bg-transparent text-sm font-sans uppercase tracking-wider transition-colors duration-100"
                        >
                            <RefreshCw className={`w-4 h-4 shrink-0 ${isLoading ? 'animate-spin' : ''}`} aria-hidden />
                        </button>
                    </div>
                </div>
            </header>

            <div className="border-b border-ir-border bg-ir-surface/80 px-5 py-2 lg:hidden min-[1280px]:px-8">
                <div className="mx-auto flex max-w-[1440px] justify-center">
                    <div className="inline-flex items-center gap-2.5 border border-ir-border bg-ir-bg/80 px-3 py-1.5">
                        <span className={`h-2 w-2 shrink-0 rounded-full ${opsStatusDot}`} aria-hidden />
                        <span className="font-sans text-xs uppercase tracking-wide text-ir-muted font-medium">{opsStatusLabel}</span>
                    </div>
                </div>
            </div>

            {/* ── BODY ── */}
            <main id="main-content" className="max-w-[1440px] mx-auto px-5 min-[1280px]:px-8 py-6 pb-14 md:pb-5">

                <p className="mb-4 font-sans text-[11px] uppercase tracking-[0.08em] text-ir-muted">
                    {isFa
                        ? 'دادهٔ شبکه · به‌روزرسانی دوره‌ای · قابل استناد برای گزارش'
                        : 'Network telemetry · periodic refresh · citation-grade reporting'}
                </p>

                {/* API error */}
                {apiError && (
                    <div className="mb-4 bg-ir-red/10 border-l-4 border-ir-red border-y border-r border-ir-border px-4 py-3 text-sm text-ir-text rounded-none">
                        <span className="text-ir-red font-sans uppercase text-[10px] tracking-wider">{isFa ? 'خطا' : 'Error'}</span>
                        <span className="text-ir-muted"> — </span>
                        {apiError}
                    </div>
                )}

                {/* ── ZONE 1: Critical status (compact strip) ── */}
                <div id="section-status" className={`status-banner rounded-none ${atmosphereBannerClass}`}>
                    <div className="status-banner__left">
                        <div className="mt-0.5 flex shrink-0 items-center justify-center" aria-hidden>
                            {isCritical || isDegraded ? (
                                <AlertTriangle
                                    className={`h-5 w-5 ${isCritical ? 'text-ir-red' : 'text-ir-amber'}`}
                                    strokeWidth={2}
                                />
                            ) : (
                                <span className={`h-2.5 w-2.5 rounded-full ${statusDotClass(tier)}`} />
                            )}
                        </div>
                        <div className="min-w-0 max-w-3xl space-y-1">
                            <div className="flex flex-wrap items-center gap-2">
                                <span
                                    className={`inline-flex items-center gap-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] ${isCritical ? 'text-ir-red' : isDegraded ? 'text-ir-amber' : 'text-ir-muted'
                                        }`}
                                >
                                    {!isCritical && !isDegraded && (
                                        <span className={`h-1.5 w-1.5 rounded-full ${statusDotClass(tier)}`} aria-hidden />
                                    )}
                                    {opsStripLabel(tier, isFa)}
                                </span>
                            </div>
                            <h2 className="font-sans text-[15px] font-semibold leading-snug tracking-tight text-ir-text">
                                {statusBannerTitle}
                            </h2>
                            {statusSubline ? (
                                <p
                                    className={`font-sans text-xs leading-snug ${isCritical ? 'text-ir-text' : 'text-ir-muted'}`}
                                >
                                    {statusSubline}
                                </p>
                            ) : (
                                <p className="font-sans text-xs leading-snug text-ir-muted">
                                    {isFa ? 'محدودهٔ عادی از دید پروب‌های خارجی' : 'Within normal range on external vantage measurements.'}
                                </p>
                            )}
                            <BannerDataFreshnessLine lastUpdate={lastUpdate} isFa={isFa} />
                        </div>
                    </div>
                    <div className="status-banner__divider" aria-hidden />
                    <div className="status-banner__right">
                        <div>
                            <span className="banner-label">{isFa ? 'آخرین در نمودار' : 'Latest in chart'}</span>
                            <span
                                className={`banner-percent ${isCritical ? 'banner-percent--outage' : isDegraded ? 'banner-percent--degraded' : 'banner-percent--normal'}`}
                            >
                                {stats != null ? `${stats.current.toFixed(1)}%` : `${Math.round(connectivity)}%`}
                            </span>
                        </div>
                        <span className="banner-meta text-right">{isFa ? 'merit-nt · بازهٔ انتخاب‌شده' : 'merit-nt · chart window'}</span>
                    </div>
                </div>

                {seriesErrors.status && (
                    <p className="text-[10px] font-sans text-ir-amber">
                        {isFa ? 'وضعیت زنده در دسترس نیست: ' : 'Live status unavailable: '}
                        <span className="font-mono text-ir-muted">{seriesErrors.status}</span>
                    </p>
                )}

                {/* ── ZONE 2: Full-width outage clock, then 4 equal KPI tiles (feet row) ── */}
                <div
                    id="section-clock"
                    className="section-major flex min-h-[min(22rem,52vh)] flex-col gap-4 [contain:layout] lg:min-h-[20rem] lg:gap-5"
                >
                    <div className="min-h-[12.5rem] min-w-0 w-full lg:min-h-[14rem]">
                        {outageClockLoading && outageClockMerit.length === 0 ? (
                            <div className="border border-ir-border bg-ir-surface/60 p-4 sm:p-5 animate-pulse rounded-none h-full min-h-[200px]">
                                <div className="h-3 w-40 bg-ir-elevated mb-3" />
                                <div className="h-12 w-full max-w-md bg-ir-elevated" />
                                <p className="text-[10px] text-ir-muted font-mono mt-4 uppercase tracking-wider">
                                    {isFa
                                        ? `بارگذاری ${OUTAGE_CLOCK_LOOKBACK_DAYS} روز · MERIT-NT`
                                        : `Loading ${OUTAGE_CLOCK_LOOKBACK_DAYS}d window · MERIT-NT`}
                                </p>
                            </div>
                        ) : outageClockError && outageClockMerit.length === 0 ? (
                            <div
                                className="border px-4 py-3 text-sm rounded-none font-sans"
                                style={{
                                    borderColor: 'var(--border)',
                                    backgroundColor: 'var(--bg-surface)',
                                    color: 'var(--text-muted)',
                                }}
                            >
                                {isFa ? 'ساعت قطع: داده‌ای برای بازهٔ ۴۵ روز نیامد.' : 'Outage clock: no data for the 45-day window.'}{' '}
                                <span className="text-ir-text font-mono text-xs">{outageClockError}</span>
                            </div>
                        ) : (
                            <SinceThirtyFiveTimer
                                connectivityPoints={outageClockMerit}
                                secondaryPoints={outageClockPing}
                                currentPct={stats?.current ?? connectivity}
                                lookbackDays={OUTAGE_CLOCK_LOOKBACK_DAYS}
                                isFa={isFa}
                                lastIngestUtc={realtimeStatus?.lastIngestUtc ?? null}
                            />
                        )}
                    </div>
                    {stats && (
                        <div className="sre-metric-strip sre-metric-strip--under-clock min-h-[5.75rem] min-w-0 w-full">
                            <div className="sre-metric-tile">
                                <span className="sre-metric-tile__label">{isFa ? 'شاخص اتصال' : 'Connectivity'}</span>
                                <span
                                    className={`sre-metric-tile__value ${connectivity < 35 ? 'text-ir-red' : connectivity < 60 ? 'text-ir-amber' : 'text-cyan-300'
                                        }`}
                                >
                                    {Math.round(connectivity)}%
                                </span>
                                <span className="sre-metric-tile__meta">{isFa ? 'merit-nt · خوانش لحظه‌ای' : 'merit-nt · live sample'}</span>
                            </div>
                            <div className="sre-metric-tile">
                                <span className="sre-metric-tile__label">{isFa ? 'میانگین بازه' : 'Window avg'}</span>
                                <span className="sre-metric-tile__value text-sky-300">{stats.avg.toFixed(1)}%</span>
                                <span className="sre-metric-tile__meta">{timeLabel}</span>
                            </div>
                            <div className="sre-metric-tile">
                                <span className="sre-metric-tile__label">{isFa ? 'استان‌ها' : 'Provinces'}</span>
                                <span
                                    className={`sre-metric-tile__value ${affectedRegionCount > 0 ? 'text-ir-amber' : 'text-slate-300'}`}
                                >
                                    {affectedRegionCount}
                                </span>
                                <span className="sre-metric-tile__meta">{isFa ? '/۳۱' : '/31'}</span>
                            </div>
                            <div className="sre-metric-tile">
                                <span className="sre-metric-tile__label">{isFa ? 'BGP' : 'BGP'}</span>
                                <span className="sre-metric-tile__value text-slate-300">
                                    {latestBgpPrefixes != null ? Math.round(latestBgpPrefixes).toLocaleString(isFa ? 'fa-IR' : 'en-US') : '—'}
                                </span>
                                <span className="sre-metric-tile__meta">{isFa ? 'پیشوند' : 'prefixes'}</span>
                            </div>
                        </div>
                    )}
                </div>

                {/* ── ZONE 3: Supporting data & trends ── */}
                <Section id="section-timeline" className="section-major p-6 relative">
                    <SectionTitle
                        icon={<div className="h-2 w-2 shrink-0 rounded-none bg-ir-blue" aria-hidden />}
                        title={isFa ? 'خط زمانی اتصال' : 'Connectivity timeline'}
                        metricKey="connectivity"
                        isFa={isFa}
                        level={1}
                        badge={<span className="text-[10px] font-mono text-ir-muted uppercase tracking-wider">{isFa ? 'merit-nt' : 'merit-nt'}</span>}
                    />
                    {seriesErrors.connectivity && (
                        <p className="mt-0 mb-2 text-[10px] font-sans text-ir-amber border border-ir-amber/40 bg-ir-amber/5 px-2 py-1">
                            {isFa ? 'دادهٔ اتصال در دسترس نیست: ' : 'Connectivity data unavailable: '}
                            <span className="font-mono text-ir-muted">{seriesErrors.connectivity}</span>
                        </p>
                    )}
                    <div className="relative mt-0 flex shrink-0 flex-wrap items-center justify-end gap-1">
                            {(['1h', '6h', '24h', '7d', '30d', '45d'] as const).map((r) => {
                                const selected = timeRange === r && !customStart;
                                const rangeAria = isFa
                                    ? ({
                                          '1h': 'بازه نمودار: ۱ ساعت',
                                          '6h': 'بازه نمودار: ۶ ساعت',
                                          '24h': 'بازه نمودار: ۲۴ ساعت',
                                          '7d': 'بازه نمودار: ۷ روز',
                                          '30d': 'بازه نمودار: ۳۰ روز',
                                          '45d': 'بازه نمودار: ۴۵ روز',
                                      } as const)[r]
                                    : ({
                                          '1h': 'Chart range: 1 hour',
                                          '6h': 'Chart range: 6 hours',
                                          '24h': 'Chart range: 24 hours',
                                          '7d': 'Chart range: 7 days',
                                          '30d': 'Chart range: 30 days',
                                          '45d': 'Chart range: 45 days',
                                      } as const)[r];
                                return (
                                <button
                                    key={r}
                                    type="button"
                                    aria-pressed={selected}
                                    aria-label={rangeAria}
                                    onClick={() => {
                                        startTransition(() => {
                                            setTimeRange(r);
                                            setCustomStart(null);
                                            setCustomEnd(null);
                                            setCustomRangeOpen(false);
                                        });
                                    }}
                                    className={`px-2 py-1 text-[10px] font-sans font-semibold uppercase tracking-wider transition-colors duration-100 rounded-none ${selected
                                            ? 'bg-blue-700 text-white'
                                            : 'border border-transparent text-ir-muted hover:border-ir-border hover:text-ir-text'
                                        }`}
                                >
                                    {r === '1h' ? '1H' : r.toUpperCase()}
                                </button>
                            );
                            })}
                            <button
                                type="button"
                                aria-pressed={Boolean(customStart && customEnd)}
                                aria-label={isFa ? 'بازه زمانی سفارشی برای نمودار' : 'Custom chart date range'}
                                aria-expanded={customRangeOpen}
                                onClick={() => {
                                    startTransition(() => {
                                        if (!customRangeOpen) {
                                            const end = new Date();
                                            const start = new Date(end.getTime() - 24 * 3600 * 1000);
                                            setPickerStart(customStart ?? start);
                                            setPickerEnd(customEnd ?? end);
                                        }
                                        setCustomRangeOpen((o) => !o);
                                    });
                                }}
                                className={`px-2 py-1 text-[10px] font-sans font-semibold uppercase tracking-wider transition-colors duration-100 rounded-none ${customStart && customEnd
                                        ? 'bg-blue-700 text-white'
                                        : 'border border-transparent text-ir-muted hover:border-ir-border hover:text-ir-text'
                                    }`}
                            >
                                {isFa ? 'بازه' : 'Custom'}
                            </button>
                            {customRangeOpen && (
                                <div className="absolute right-0 top-full mt-2 z-50 w-[min(100vw-2rem,28rem)]">
                                    <Suspense
                                        fallback={
                                            <div className="rounded-lg border border-ir-border bg-ir-surface p-4 text-xs text-ir-muted">
                                                {isFa ? 'بارگذاری تقویم…' : 'Loading calendar…'}
                                            </div>
                                        }
                                    >
                                        <DateRangePicker
                                            compact
                                            startDate={pickerStart}
                                            endDate={pickerEnd}
                                            onStartDateChange={setPickerStart}
                                            onEndDateChange={setPickerEnd}
                                            language={language}
                                            onApply={() => {
                                                if (pickerStart && pickerEnd) {
                                                    startTransition(() => {
                                                        setCustomStart(pickerStart);
                                                        setCustomEnd(pickerEnd);
                                                        setCustomRangeOpen(false);
                                                    });
                                                }
                                            }}
                                            onReset={() => {
                                                startTransition(() => {
                                                    setPickerStart(null);
                                                    setPickerEnd(null);
                                                    setCustomStart(null);
                                                    setCustomEnd(null);
                                                    setCustomRangeOpen(false);
                                                });
                                            }}
                                        />
                                    </Suspense>
                                </div>
                            )}
                    </div>
                    <div
                        ref={chartCaptureRef}
                        id="irinter-main-chart"
                        className="mt-3 h-[260px] sm:h-[300px] min-h-[260px] sm:min-h-[300px]"
                    >
                        <Suspense
                            fallback={
                                <div className="h-full flex items-center justify-center border border-dashed border-ir-border bg-ir-bg/40">
                                    <div className="w-6 h-6 border-2 border-ir-blue border-t-transparent rounded-full animate-spin" />
                                </div>
                            }
                        >
                            <LazyMeritConnectivityChartPanel
                                isLoading={isLoading}
                                hasMeritData={chartData.length > 0}
                                isFa={isFa}
                                meritChartPlotData={meritChartPlotData}
                                connectivityXDomain={connectivityXDomain}
                                formatXAxis={formatXAxis}
                            />
                        </Suspense>
                    </div>
                </Section>

                {/* ── 4. RESPONSE TIME ── */}
                <Section className="section-major p-6">
                    <SectionTitle
                        icon={<div className="h-1.5 w-1.5 shrink-0 rounded-none bg-[#10B981]" aria-hidden />}
                        title={isFa ? 'زمان پاسخ شبکه' : 'Network Response Time'}
                        metricKey="latency"
                        isFa={isFa}
                        level={2}
                    />
                    <Suspense
                        fallback={
                            <div className="relative mt-4 h-[240px] sm:h-[300px] flex items-center justify-center border border-dashed border-ir-border/40 bg-ir-bg/20">
                                <div className="w-6 h-6 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
                            </div>
                        }
                    >
                        <LazyLatencyChartPanel
                            isLoading={isLoading}
                            latencyChartData={latencyChartData}
                            formatXAxis={formatXAxis}
                            isFa={isFa}
                            seriesError={seriesErrors.latency}
                        />
                    </Suspense>
                </Section>

                {/* ── 5. PACKET LOSS · ROUTING · TRAFFIC LEVEL (supporting signals) ── */}
                <div className="section-major">
                    <p className="section-zoom-label !mb-0">{isFa ? 'سیگنال‌های پشتیبان' : 'Supporting signals'}</p>
                    <p className="section-subtle mb-4">
                        {isFa
                            ? 'زمینه برای خوانش شاخص اتصال در بالا'
                            : 'Context for the connectivity reading above'}
                    </p>
                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                        {/* Packet Loss */}
                        <Section className="p-6">
                            <SectionTitle
                                icon={<div className="h-1.5 w-1.5 shrink-0 rounded-none bg-[#F43F5E]" aria-hidden />}
                                title={isFa ? 'تلفات بسته' : 'Packet Loss'}
                                metricKey="packetLoss"
                                isFa={isFa}
                                level={2}
                            />
                            <Suspense
                                fallback={
                                    <div className="relative mt-3 h-[180px] flex items-center justify-center border border-dashed border-ir-border/40">
                                        <div className="w-5 h-5 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" />
                                    </div>
                                }
                            >
                                <LazyPacketLossChartPanel
                                    isLoading={isLoading}
                                    packetLossChartData={packetLossChartData}
                                    formatXAxis={formatXAxis}
                                    isFa={isFa}
                                    seriesError={seriesErrors.packetLoss}
                                />
                            </Suspense>
                        </Section>

                        {/* BGP Routing */}
                        <Section className="p-6">
                            <SectionTitle
                                icon={<div className="h-1.5 w-1.5 shrink-0 rounded-none bg-[#F59E0B]" aria-hidden />}
                                title={isFa ? 'مسیریابی (BGP)' : 'Routing (BGP)'}
                                metricKey="bgpRouting"
                                isFa={isFa}
                                level={2}
                            />
                            <Suspense
                                fallback={
                                    <div className="relative mt-3 h-[180px] flex items-center justify-center border border-dashed border-ir-border/40">
                                        <div className="w-5 h-5 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
                                    </div>
                                }
                            >
                                <LazyBgpChartPanel
                                    isLoading={isLoading}
                                    bgpChartData={bgpChartData}
                                    formatXAxis={formatXAxis}
                                    isFa={isFa}
                                    seriesError={seriesErrors.bgp}
                                />
                            </Suspense>
                        </Section>

                        {/* Traffic Level */}
                        <Section className="p-6">
                            <SectionTitle
                                icon={<div className="h-1.5 w-1.5 shrink-0 rounded-none bg-[#8B5CF6]" aria-hidden />}
                                title={isFa ? 'سطح ترافیک' : 'Traffic Level'}
                                metricKey="trafficLevel"
                                isFa={isFa}
                                level={2}
                            />
                            <div className="-mt-1 mb-2 flex flex-wrap items-center gap-2 text-[10px]">
                                <span className="text-ir-muted uppercase tracking-wider">
                                    {isFa ? 'بازهٔ نمودار' : 'Chart range'}
                                </span>
                                {(['7d', '30d', '45d'] as const).map((r) => {
                                    const selected = timeRange === r && !customStart;
                                    const label =
                                        r === '7d'
                                            ? (isFa ? '۷ روز' : '7D')
                                            : r === '30d'
                                              ? (isFa ? '۳۰ روز' : '30D')
                                              : (isFa ? '۴۵ روز' : '45D');
                                    return (
                                        <button
                                            key={r}
                                            type="button"
                                            aria-pressed={selected}
                                            aria-label={isFa ? `سطح ترافیک: ${label}` : `Traffic level: ${label}`}
                                            onClick={() =>
                                                startTransition(() => {
                                                    setTimeRange(r);
                                                    setCustomStart(null);
                                                    setCustomEnd(null);
                                                    setCustomRangeOpen(false);
                                                })
                                            }
                                            className={
                                                selected
                                                    ? 'border border-violet-500/70 bg-violet-500/15 px-2 py-0.5 font-mono text-ir-text'
                                                    : 'border border-ir-border px-2 py-0.5 font-mono text-ir-muted hover:border-ir-muted hover:text-ir-text'
                                            }
                                        >
                                            {label}
                                        </button>
                                    );
                                })}
                                <span className="text-ir-faint hidden sm:inline">
                                    {isFa ? '(همان بازهٔ بالای صفحه)' : '(same as top timeline)'}
                                </span>
                            </div>
                            <Suspense
                                fallback={
                                    <div className="relative mt-3 h-[180px] flex items-center justify-center border border-dashed border-ir-border/40">
                                        <div className="w-5 h-5 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
                                    </div>
                                }
                            >
                                <LazyTrafficLevelDualChartPanel
                                    isLoading={isLoading}
                                    trafficLevelDualChartData={trafficLevelDualChartData}
                                    hasTrafficOrWebSearch={
                                        trafficLevelChartData.length > 0 || webSearchLevelChartData.length > 0
                                    }
                                    formatXAxis={formatXAxis}
                                    isFa={isFa}
                                    seriesErrorGtr={seriesErrors.gtr}
                                    seriesErrorWebSearch={seriesErrors.gtrWebSearch}
                                />
                            </Suspense>
                        </Section>
                    </div>
                </div>

                {/* ── 6. TRAFFIC VOLUME ── */}
                <Section className="section-major p-6">
                    <SectionTitle
                        icon={<div className="h-1.5 w-1.5 shrink-0 rounded-none bg-[#6366F1]" aria-hidden />}
                        title={isFa ? 'حجم ترافیک شبکه' : 'Network Traffic Volume'}
                        metricKey="traffic"
                        isFa={isFa}
                        level={2}
                    />
                    <Suspense
                        fallback={
                            <div className="mt-4 h-[240px] sm:h-[300px] flex items-center justify-center border border-dashed border-ir-border/40 bg-ir-bg/20">
                                <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                            </div>
                        }
                    >
                        <LazyTrafficVolumeChartPanel
                            isLoadingExtra={isLoadingExtra}
                            trafficChartData={trafficChartData}
                            formatXAxis={formatXAxis}
                            isFa={isFa}
                        />
                    </Suspense>
                </Section>

                {/* ── 6. OUTAGE REPORTS ── */}
                <Section className="section-major p-6" id="panel-outage-reports">
                    <SectionTitle
                        icon={<div className="h-1.5 w-1.5 shrink-0 rounded-none bg-[#FB7185]" aria-hidden />}
                        title={isFa ? 'گزارش قطعی‌ها' : 'Outage Reports'}
                        metricKey="outages"
                        isFa={isFa}
                        level={2}
                        badge={outageAnnotations.length > 0 ? (
                            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-rose-500/10 text-rose-400 border border-rose-500/20">{outageAnnotations.length}</span>
                        ) : undefined}
                    />
                    <div className="mt-3">
                        {isLoadingExtra ? (
                            <div className="h-20 flex items-center justify-center"><div className="w-6 h-6 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" /></div>
                        ) : outageAnnotations.length > 0 ? (
                            <div className="space-y-2 max-h-[280px] overflow-y-auto">
                                {outageAnnotations.slice(0, 15).map((o, i) => {
                                    const primary = humanizeOutageClassification(o.cause ?? o.scope, isFa);
                                    const causeU = (o.cause ?? '').replace(/_/g, '').toUpperCase();
                                    const scopeU = (o.scope ?? '').replace(/_/g, '').toUpperCase();
                                    const typeU = (o.outageType ?? '').replace(/_/g, '').toUpperCase();
                                    let pill: string | null = null;
                                    if (o.outageType && typeU && typeU !== causeU) {
                                        pill = o.outageType.replace(/_/g, ' ').trim().toUpperCase();
                                    } else if (o.scope && scopeU && scopeU !== causeU) {
                                        pill = o.scope.replace(/_/g, ' ').trim().toUpperCase();
                                    }
                                    return (
                                        <div
                                            key={i}
                                            className="report-row flex flex-col gap-2 border border-ir-border bg-ir-elevated/50 p-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4 rounded-[4px]"
                                        >
                                            <div className="min-w-0 flex-1">
                                                <div className="report-meta font-data-mono text-xs" style={{ color: 'var(--text-muted)' }}>
                                                    {o.startDate ? formatOutageReportUtc(o.startDate, isFa) : '—'}
                                                    {o.endDate ? ` — ${formatOutageReportUtc(o.endDate, isFa)}` : ''}
                                                </div>
                                                <div className="report-body mt-2 flex flex-wrap items-center gap-2">
                                                    <span className="report-title font-sans text-sm font-medium leading-snug" style={{ color: 'var(--text-primary)' }}>
                                                        {primary}
                                                    </span>
                                                    {pill && (
                                                        <span className="report-scope-pill inline-block rounded border border-red-400/40 bg-red-500/15 px-1.5 py-0.5 font-sans text-[10px] font-medium uppercase tracking-wide text-red-200">
                                                            {pill}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            {o.linkedUrl && (
                                                <a
                                                    href={o.linkedUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="report-details-link shrink-0 font-sans text-xs text-ir-blue hover:underline sm:pt-0.5"
                                                >
                                                    {isFa ? 'جزئیات ←' : 'Details →'}
                                                </a>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <p className="text-sm text-ir-muted text-center py-6">{isFa ? 'قطعی ثبت‌شده‌ای در این بازه وجود ندارد.' : 'No outage reports recorded in this period.'}</p>
                        )}
                    </div>
                </Section>

                {/* ── 7. REGIONAL STATUS — geographic impact ── */}
                <div className="section-major">
                    <h2 className="section-zoom-label">{isFa ? 'تأثیر جغرافیایی' : 'Geographic impact'}</h2>
                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-5 lg:gap-4">
                        {/* Province outage map */}
                        <Section className="p-6 lg:col-span-3">
                            <SectionTitle
                                icon={<div className="h-2 w-2 shrink-0 rounded-none bg-ir-blue" aria-hidden />}
                                title={isFa ? 'وضعیت استانی' : 'Provincial Status'}
                                metricKey="regions"
                                isFa={isFa}
                                level={1}
                                badge={affectedRegionCount > 0 ? (
                                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-red-500/10 text-red-400 border border-red-500/20">
                                        {affectedRegionCount} {isFa ? 'تحت تأثیر' : 'affected'}
                                    </span>
                                ) : undefined}
                            />
                            <div className="mt-3 min-h-[300px] min-w-0 w-full lg:min-w-[420px]">
                                <IranMap regionData={regionData} isFa={isFa} />
                                {regionData.length === 0 && (
                                    <p className="text-xs text-ir-muted text-center mt-2">{isFa ? 'داده در حال بارگذاری...' : 'Loading data...'}</p>
                                )}
                            </div>
                        </Section>

                        {/* Affected networks (ASN / geo) */}
                        <Section id="section-geo" className="p-6 lg:col-span-2">
                            <SectionTitle
                                icon={<div className="h-1.5 w-1.5 shrink-0 rounded-none bg-ir-amber" aria-hidden />}
                                title={isFa ? 'مراکز داده تحت تأثیر' : 'Affected networks'}
                                isFa={isFa}
                                level={2}
                                badge={geoAsnData.length > 0 ? (
                                    <span className="text-[10px] text-ir-muted">{geoAsnData.length} {isFa ? 'مورد' : 'entries'}</span>
                                ) : undefined}
                            />
                            <div className="mt-3">
                                {geoAsnData.length > 5 && (
                                    <div className="relative mb-3">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-ir-muted" />
                                        <input
                                            type="text" value={dcFilter} onChange={e => setDcFilter(e.target.value)}
                                            placeholder={isFa ? 'جستجو ASN یا سازمان...' : 'Filter by ASN or org...'}
                                            className="w-full rounded-none border border-ir-border bg-ir-bg py-1.5 pl-9 pr-3 text-xs text-ir-text placeholder:text-ir-faint focus:ring-1 focus:ring-amber-500/30"
                                        />
                                    </div>
                                )}
                                {filteredGeoAsnData.length > 0 ? (
                                    <div className="overflow-x-auto max-h-[360px] overflow-y-auto">
                                        <table className="w-full text-xs">
                                            <thead>
                                                <tr className="text-left text-ir-muted uppercase tracking-[0.12em] border-b border-ir-border">
                                                    <th className="py-2 px-3 text-[10px] font-medium font-sans">{isFa ? 'شبکه' : 'Network'}</th>
                                                    <th className="py-2 px-3 text-right text-[10px] font-medium font-sans">{isFa ? 'وضعیت' : 'Status'}</th>
                                                    <th className="py-2 px-3 text-right text-[10px] font-medium font-sans">{isFa ? 'امتیاز' : 'Score'}</th>
                                                    <th className="py-2 px-3 text-right text-[10px] font-medium font-sans">{isFa ? 'رویدادها' : 'Events'}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredGeoAsnData.map(row => (
                                                    <tr key={row.entity_code} className="border-b border-ir-border/60 hover:bg-ir-elevated/40">
                                                        <td className="py-2 px-3">
                                                            <span className="font-mono text-amber-400 text-[11px]">{row.asn ?? row.entity_code}</span>
                                                            <div className="text-ir-muted truncate max-w-[180px]">{row.org ?? row.entity_name?.split('--')[0]?.trim() ?? ''}</div>
                                                        </td>
                                                        <td className="py-2 px-3 text-right">
                                                            <StatusPill status={scoreToNetworkStatus(row.score, row.event_cnt)} language={language} />
                                                        </td>
                                                        <td className="py-2 px-3 text-right text-red-400 font-semibold">{row.score >= 1000 ? row.score.toLocaleString() : row.score.toFixed(1)}</td>
                                                        <td className="py-2 px-3 text-right text-ir-muted">{row.event_cnt}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <p className="text-xs text-ir-muted text-center py-4">{isFa ? 'مرکز داده تحت تأثیری ثبت نشده' : 'No affected networks recorded'}</p>
                                )}
                            </div>
                        </Section>
                    </div>
                </div>

                {/* ── 8. GEOGRAPHICAL DISTRIBUTION ── */}
                <Section className="section-major p-6">
                    <SectionTitle
                        icon={<div className="h-1.5 w-1.5 shrink-0 rounded-none bg-sky-400" aria-hidden />}
                        title={isFa ? 'پراکندگی جغرافیایی ترافیک' : 'Traffic Distribution by Province'}
                        metricKey="geoTraffic"
                        isFa={isFa}
                        level={2}
                    />
                    <div className="mt-4">
                        {isLoadingExtra ? (
                            <div className="h-40 flex items-center justify-center"><div className="w-6 h-6 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" /></div>
                        ) : trafficByProvinceData.length > 0 ? (
                            <div className="flex flex-col lg:flex-row gap-6">
                                <div className="flex-1 min-w-0">
                                    <IranMap regionData={[]} trafficData={trafficByProvinceData} isFa={isFa} className="max-h-[360px]" />
                                </div>
                                <div className="w-full lg:w-64 shrink-0 overflow-auto max-h-[400px]">
                                    <table className="w-full text-xs">
                                        <thead>
                                            <tr className="border-b border-ir-border text-ir-muted">
                                                <th className="text-left py-1.5 pr-3">{isFa ? 'استان' : 'Province'}</th>
                                                <th className="text-right py-1.5">{isFa ? 'درصد' : '%'}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {trafficByProvinceData.slice(0, 20).map((row, i) => (
                                                <tr key={i} className="border-b border-ir-border/50">
                                                    <td className="py-1.5 pr-3 text-ir-text">{row.regionName}</td>
                                                    <td className="py-1.5 text-right text-sky-300 tabular-nums font-medium">
                                                        {row.value < 0.1 ? '< 0.1%' : `${row.value.toFixed(1)}%`}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        ) : (
                            <p className="text-sm text-ir-muted text-center py-6">{isFa ? 'داده‌ای موجود نیست' : 'No data available'}</p>
                        )}
                    </div>
                </Section>

                {/* ── ML INSIGHTS (code-split: avoids blocking INP on first paint) ── */}
                <div id="section-ml" className="section-major min-h-[min(28rem,55vh)]">
                    <Suspense
                        fallback={
                            <div
                                className="rounded-[4px] border border-ir-border bg-ir-surface min-h-[20rem] animate-pulse"
                                aria-hidden
                            />
                        }
                    >
                        <MLInsightsPanel language={language} topGeoAsn={geoAsnData.slice(0, 8)} />
                    </Suspense>
                </div>

                <section
                    id="section-export"
                    tabIndex={-1}
                    aria-labelledby="section-export-title"
                    className="h-px w-full shrink-0 scroll-mt-20 outline-none max-md:scroll-mb-14"
                >
                    <h2 id="section-export-title" className="sr-only">
                        {isFa ? 'خروجی CSV و PDF' : 'CSV and PDF export'}
                    </h2>
                </section>
            </main>

            <nav
                className="fixed bottom-0 left-0 right-0 z-40 flex h-[52px] items-center justify-around border-t border-ir-border bg-ir-surface md:hidden pb-[max(0px,env(safe-area-inset-bottom))]"
                aria-label={isFa ? 'قطعی‌ها و خروجی' : 'Incidents and exports'}
            >
                <Link
                    to="/incidents"
                    className="flex flex-col items-center gap-0.5 text-[10px] font-sans uppercase tracking-wider text-ir-muted hover:text-ir-text"
                >
                    <ListTree className="h-4 w-4" aria-hidden />
                    {isFa ? 'قطعی‌ها' : 'Incidents'}
                </Link>
                <button
                    type="button"
                    onClick={() => void handleExportCsv()}
                    className="flex flex-col items-center gap-0.5 text-[10px] font-sans uppercase tracking-wider text-ir-muted hover:text-ir-text"
                >
                    <Download className="h-4 w-4" aria-hidden />
                    CSV
                </button>
                <button
                    type="button"
                    disabled={pdfExporting}
                    onClick={() => void handleExportPdf()}
                    className="flex flex-col items-center gap-0.5 text-[10px] font-sans uppercase tracking-wider text-ir-muted hover:text-ir-text disabled:opacity-50"
                >
                    {pdfExporting ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Download className="h-4 w-4" aria-hidden />}
                    PDF
                </button>
            </nav>

            {toast && (
                <div
                    className={`fixed z-[100] left-3 right-3 md:left-auto md:right-4 md:max-w-md bottom-[60px] md:bottom-4 border border-ir-border bg-ir-elevated p-3 shadow-2xl text-xs font-sans text-ir-text ${toast.kind === 'csv-ok' || toast.kind === 'pdf-ok'
                            ? 'border-l-4 border-l-ir-green'
                            : toast.kind === 'pdf-gen'
                                ? 'border-l-4 border-l-ir-blue'
                                : toast.kind === 'pdf-err'
                                    ? 'border-l-4 border-l-ir-red'
                                    : 'border-l-4 border-l-ir-amber'
                        }`}
                    role="status"
                >
                    {toast.kind === 'csv-ok' && (
                        <p>
                            {isFa ? 'CSV دانلود شد — ' : 'CSV downloaded — '}
                            <span className="font-mono">{toast.filename}</span>
                        </p>
                    )}
                    {toast.kind === 'pdf-gen' && (
                        <p className="flex items-center gap-2">
                            <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
                            {isFa ? 'در حال تولید PDF…' : 'Generating PDF brief…'}
                        </p>
                    )}
                    {toast.kind === 'pdf-ok' && (
                        <p>
                            {isFa ? 'PDF ذخیره شد — ' : 'PDF saved — '}
                            <span className="font-mono">{toast.filename}</span>
                        </p>
                    )}
                    {toast.kind === 'pdf-warn' && <p className="text-ir-amber">{toast.text}</p>}
                    {toast.kind === 'pdf-err' && (
                        <div>
                            <p className="text-ir-red">{isFa ? 'خطا در تولید PDF.' : 'PDF generation failed.'}</p>
                            <button
                                type="button"
                                className="mt-1 text-[10px] text-ir-muted underline"
                                onClick={() =>
                                    setToast((prev) =>
                                        prev?.kind === 'pdf-err' ? { ...prev, expanded: !prev.expanded } : prev
                                    )
                                }
                            >
                                {isFa ? 'جزئیات ▾' : 'Details ▾'}
                            </button>
                            {toast.expanded && (
                                <pre className="mt-2 max-h-40 overflow-auto text-[10px] text-ir-muted whitespace-pre-wrap break-all">
                                    {toast.message}
                                </pre>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
